import React, { useState } from "react";
import {
    Avatar,
    Card,
    CardActions,
    CardContent,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from "@material-ui/core";
import { Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import PerfectScrollbar from "react-perfect-scrollbar";
import useReactRouter from "use-react-router";
import { RouteComponentProps } from "react-router-dom";
import moment from "moment";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { Page } from "../../../../../components/Page";
import PageHeader from "../../../../../components/PageHeader";
import SearchBar from "../../../../../components/SearchBar";
import SearchFilter from "../../../UserList/SearchFilter";
import { LoadingOverlay } from "../../../../../components/LoadingOverlay";
import {
    OPERATOR,
    PaginatiorInfo,
    WhereConstraints
} from "../../../../../graphql/common.datatypes";
import { FilterValues } from "../../../UserList/UserList";
import getUsers from "../../../getUsers.api";
import { User } from "../../../../../graphql/auth";
import getInitials from "../../../../../utils/getInitials";
import ConfirmModal from "../../../../../components/ConfirmModal";
import mergeUserMutation, { MergeUserInput } from "./api/mergeUser";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3),
        position: "relative"
    },
    results: {
        marginTop: theme.spacing(3)
    },
    filter: {
        width: "100%",
        height: 30
    },
    content: {
        padding: 0
    },
    inner: {
        [theme.breakpoints.up("md")]: {
            minWidth: 700
        }
    },
    nameCell: {
        display: "flex",
        alignItems: "center"
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: "flex-end"
    },
    buttonIcon: {
        marginRight: theme.spacing(1)
    }
}));

const avatar = require("../../../../../../assets/img/avatar.svg");

interface Props {
    user: User;
}

const MergeUser = (props: Props) => {
    const classes = useStyles();
    const title = "Merge User";
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [page, setPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const { match }: RouteComponentProps<{ id: string }> = useReactRouter();
    const { id } = match.params;
    const [postMergeUser, mergeUserData] = mergeUserMutation();
    const { enqueueSnackbar } = useSnackbar();

    const handleSearch = (value: string) => {
        const whereConstraints: WhereConstraints = {
            OR: [
                {
                    column: "first_name",
                    operator: OPERATOR.LIKE,
                    value: `%${value}%`
                },
                {
                    column: "last_name",
                    operator: OPERATOR.LIKE,
                    value: `%${value}%`
                },
                {
                    column: "email",
                    operator: OPERATOR.LIKE,
                    value: `%${value}%`
                }
            ]
        };

        setFilters(initialFilters);
        setWhere(whereConstraints);
    };

    const initialFilters: FilterValues = {
        email: "",
        first_name: "",
        last_name: "",
        cellphone: "",
        created_after: moment("2019-01-01T00:00").toDate(),
        role: "",
        roles: new Array<string>(),
        emailVerifiedTrue: false,
        emailVerifiedFalse: false,
        adminVerifiedTrue: false,
        adminVerifiedFalse: false,
        disabledTrue: false,
        disabledFalse: false
    };

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        const AND: WhereConstraints[] = [];

        if (newFilters.email.length > 0)
            AND.push({
                column: "email",
                operator: OPERATOR.LIKE,
                value: `%${newFilters.email}%`
            });
        if (newFilters.first_name.length > 0)
            AND.push({
                column: "first_name",
                operator: OPERATOR.LIKE,
                value: `%${newFilters.first_name}%`
            });
        if (newFilters.last_name.length > 0)
            AND.push({
                column: "last_name",
                operator: OPERATOR.LIKE,
                value: `%${newFilters.last_name}%`
            });
        if (newFilters.cellphone.length > 0)
            AND.push({
                column: "cellphone",
                operator: OPERATOR.LIKE,
                value: `%${newFilters.cellphone}%`
            });
        if (newFilters.emailVerifiedTrue)
            AND.push({
                column: "email_verified",
                operator: OPERATOR.EQ,
                value: true
            });
        if (newFilters.emailVerifiedFalse)
            AND.push({
                column: "email_verified",
                operator: OPERATOR.EQ,
                value: false
            });
        if (newFilters.adminVerifiedTrue)
            AND.push({
                column: "admin_verified",
                operator: OPERATOR.EQ,
                value: true
            });
        if (newFilters.adminVerifiedFalse)
            AND.push({
                column: "admin_verified",
                operator: OPERATOR.EQ,
                value: false
            });
        if (newFilters.disabledTrue)
            AND.push({
                column: "disabled",
                operator: OPERATOR.EQ,
                value: true
            });
        if (newFilters.disabledFalse)
            AND.push({
                column: "disabled",
                operator: OPERATOR.EQ,
                value: false
            });

        return {
            AND
        };
    };

    const [filters, setFilters] = useState(initialFilters);
    const [where, setWhere] = useState<WhereConstraints>(
        getConstraintsFromFilters(initialFilters)
    );
    const [orderBy, setOrderBy] = useState([
        { column: "last_name", order: "ASC" }
    ]);
    const usersData = getUsers(rowsPerPage, page, where, orderBy);

    const onSelected = (user: User) => {
        setSelectedUser(user);
    };

    const mergeUser = () => {
        if (!selectedUser) return;

        const sanitisedData: MergeUserInput = {
            data: {
                toUserId: parseInt(id),
                fromUserId: selectedUser.id
            }
        };

        postMergeUser({ variables: sanitisedData })
            .then(res => {
                enqueueSnackbar("User merged successfully", {
                    variant: "success"
                });
                usersData.refetch();
            })
            .catch(err => {
                enqueueSnackbar("Error merging user", {
                    variant: "error"
                });
            });
    };

    return (
        <React.Fragment>
            <Card>
                <Page className={classes.root} title={title}>
                    <PageHeader
                        section={title}
                        title={"Search For User To Merge"}
                    />
                    <SearchBar
                        onSearch={handleSearch}
                        filterContent={
                            <SearchFilter
                                applyFilters={applyFilters}
                                filters={filters}
                                initialFilters={initialFilters}
                            />
                        }
                    />
                    <LoadingOverlay loading={usersData.loading}>
                        {usersData.data && usersData.data.users && (
                            <Results
                                className={classes.results}
                                users={usersData.data.users.data}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                paginatorInfo={
                                    usersData.data.users.paginatorInfo
                                }
                                setPage={setPage}
                                onSelected={onSelected}
                            />
                        )}
                    </LoadingOverlay>
                </Page>
            </Card>

            {selectedUser && (
                <ConfirmModal
                    onClose={() => {
                        setSelectedUser(null);
                    }}
                    onConfirmClick={() => {
                        mergeUser();
                    }}
                    open={true}
                    title="Merge User"
                    message={`Are you sure you want to merge ${
                        selectedUser.first_name
                    } ${selectedUser.last_name} (${selectedUser.email}) into ${
                        props.user.first_name
                    } ${props.user.last_name} (${props.user.email})?`}
                />
            )}
        </React.Fragment>
    );
};

const Results = (props: {
    className?: string;
    users: User[];
    paginatorInfo: PaginatiorInfo;
    setPage: (page: number) => void;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    onSelected: (user: User) => void;
}) => {
    const {
        className,
        rowsPerPage,
        setPage,
        setRowsPerPage,
        paginatorInfo,
        users,
        ...rest
    } = props;
    const classes = useStyles();
    const { match }: RouteComponentProps<{ id: string }> = useReactRouter();
    const { id } = match.params;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => {
        setPage(page + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
    };

    return (
        <div {...rest} className={clsx(classes.root, className)}>
            <Typography color="textSecondary" gutterBottom variant="body2">
                Page {paginatorInfo.currentPage} of {paginatorInfo.lastPage}
            </Typography>
            <Card>
                <Divider />
                <CardContent className={classes.content}>
                    <PerfectScrollbar>
                        <div className={classes.inner}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users
                                        .filter(
                                            user => user.id !== parseInt(id)
                                        )
                                        .map(user => (
                                            <TableRow
                                                hover
                                                key={user.id}
                                                onClick={() => {
                                                    props.onSelected(user);
                                                }}
                                            >
                                                <TableCell>
                                                    <div
                                                        className={
                                                            classes.nameCell
                                                        }
                                                    >
                                                        <Avatar
                                                            className={
                                                                classes.avatar
                                                            }
                                                            src={avatar}
                                                        >
                                                            {getInitials(
                                                                user.first_name +
                                                                    " " +
                                                                    user.last_name
                                                            )}
                                                        </Avatar>
                                                        <div>
                                                            {user.last_name},{" "}
                                                            {user.first_name}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {user.email}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </div>
                    </PerfectScrollbar>
                </CardContent>
                <CardActions className={classes.actions}>
                    <TablePagination
                        component="div"
                        count={paginatorInfo.total}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        page={paginatorInfo.currentPage - 1}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </CardActions>
            </Card>
        </div>
    );
};

export default MergeUser;
